import React, { useEffect, useRef, useState } from 'react';
import { ThemeProvider, theme } from '@primer/react';
import { Router } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import ReactTooltip from 'react-tooltip';
import { useSelector } from 'react-redux';

import LTCommonHeader from '@lambdatestincprivate/lt-common-header';
import Loader from 'new-theme/components/Loader';
import Routes from '../../routes';
import history from 'utils/history';

import CustomHeader from './components/custom-header';

import usePlan from 'hooks/usePlan';
import AppBannerAndModals from './modals';
import useFeatureFlags from 'hooks/useFeatureFlags';

// import ErrorBoundary from 'new-theme/components/ErrorBoundary';

import { toasterStyle, TOOLTIP_CONTAINER_ID } from './constants';
import { FF_DISABLE_NEW_THEME } from 'constants/feature-flags';

import './style.css';
import useAppWithHeader from './useAppWithHeader';
import useApp from 'hooks/useApp';

function App() {
  const { userInfo = {}, orgPreference = {} } = useApp();
  const featureFlags = useFeatureFlags();

  const { data: userInfoData } = userInfo;

  const { error: opError } = orgPreference;

  const areFeatureFlagsLoading = featureFlags?.isLoading;

  const isThingsLoading =
    !userInfoData || opError === null || areFeatureFlagsLoading;

  const showNewTheme = !useSelector(
    (state) => state?.featureFlag?.[FF_DISABLE_NEW_THEME]
  );

  const timeRef = useRef(0);
  const clickRef = useRef(0);

  const [forceOldTheme, setForceOldTheme] = useState(false);

  function handleClick(e) {
    e.stopPropagation();
    const timeToMatch = timeRef.current;
    const curTime = new Date().getTime();
    const dif = curTime - timeToMatch;

    const header = document.getElementsByClassName('common-header-title')[0];
    let isRightElement = e.target.contains(header);

    if (clickRef.current >= 4 && dif <= 1000 && isRightElement) {
      setForceOldTheme((val) => !val);
      clickRef.current = 0;
      timeRef.current = 0;
    } else {
      timeRef.current = new Date().getTime();
      clickRef.current += 1;
    }
  }

  useEffect(() => {
    let header;
    setTimeout(() => {
      header = document.getElementsByClassName('common-header-title')[0];
      header?.addEventListener('click', handleClick);
    }, 3000);

    return () => {
      header?.removeEventListener('click', handleClick);
      clickRef.current = 0;
      timeRef.current = 0;
    };
  }, []);

  if (isThingsLoading) {
    return <Loader isSuspenseLoader={true} />;
  }

  let appContent = (
    <>
      <ReactTooltip id={TOOLTIP_CONTAINER_ID} html={true} />
      <Toaster containerStyle={toasterStyle} position="top-right" />
      <AppWithHeader showNewTheme={showNewTheme}>
        <Content forceOldTheme={forceOldTheme} />
      </AppWithHeader>
    </>
  );

  if (showNewTheme && !forceOldTheme) {
    appContent = <ThemeProvider theme={theme}>{appContent}</ThemeProvider>;
  }
  return appContent;
}

export default App;

function AppWithHeader({ children, showNewTheme }) {
  const { concurrencyToRender } = useAppWithHeader();

  return (
    <LTCommonHeader
      customProductHeader={<CustomHeader />}
      concurrencyInfo={concurrencyToRender}
      showTunnel={true}
      isNewThemeEnabled={showNewTheme}
    >
      {children}
    </LTCommonHeader>
  );
}

function Content({ forceOldTheme }) {
  const { promoBannerHeight } = usePlan();

  const showNewTheme = !useSelector(
    (state) => state?.featureFlag?.[FF_DISABLE_NEW_THEME]
  );

  let className = 'main-section';

  if (showNewTheme && !forceOldTheme) {
    className += ' new-theme';
  }

  className +=
    promoBannerHeight > 0
      ? ' newHeaderContentWrapperWithBanner'
      : ' newHeaderContentWrapper';

  return (
    <React.Suspense fallback={<Loader isSuspenseLoader={true} />}>
      <Router history={history}>
        <div className={className} id="app-main-section">
          <AppBannerAndModals />
          <Routes forceOldTheme={forceOldTheme} history={history} />
        </div>
      </Router>
    </React.Suspense>
  );
}
